export const capitalizeFirstLetter = (string: string) => {
    if(!string){
      return '';
    }
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function seperatedStringToArray(string: string, seperator: string): string[] {
  return string.split(seperator).map(value => value.trim()).filter(org => org);
}

// Latin letters that Unicode gives no decomposition for, so NFKD leaves them intact and the
// [a-z0-9] filter below would eat them. Everything else accented (é, ä, č, ñ…) needs no entry here —
// NFKD splits it into an ASCII base letter plus a combining mark, and the mark is dropped.
const LATIN_FOLDS: Record<string, string> = {
  'ß': 'ss', 'æ': 'ae', 'œ': 'oe', 'ø': 'o', 'ð': 'd', 'þ': 'th', 'đ': 'd',
  'ħ': 'h', 'ŧ': 't', 'ł': 'l', 'ŀ': 'l', 'ŋ': 'n', 'ŉ': 'n', 'ı': 'i', 'ĸ': 'k',
};

/**
 * Turns a display name into a brand/account key (DEV-545). The output is kept in sync with
 * `CustomValidators.brandKey`: lowercase letters, digits and single underscores, no leading or
 * trailing underscore — so a generated key never lands in the form already invalid.
 *
 * Every run of unsupported characters collapses into one underscore, which is why `Ben & Jerry's`
 * becomes `ben_jerry_s` rather than `ben___jerry_s`. A name with nothing key-safe in it (e.g. all
 * non-Latin) yields an empty string; callers decide what to do with that.
 *
 * Latin letters are folded to their ASCII base first, so `Nestlé` becomes `nestle` rather than `nestl`
 * and `Ærø` becomes `aero` rather than `r`. Without that step those letters are outside `[a-z0-9]`, so
 * they get dropped or turned into an underscore mid-word — a mangled key that still passes validation
 * and so goes unnoticed, which is worse than an empty one given the key is immutable once created.
 */
export function toEntityKey(value: string): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    // NFKD splits é into `e` + a combining accent, and folds full-width `ａ` onto plain `a`; dropping
    // the combining marks then leaves the ASCII base letter behind.
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, char => LATIN_FOLDS[char] ?? char)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}
